import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  FaPlus,
  FaPaperPlane,
  faSpinner,
  FaXmark,
  faCheck,
  FaWhatsapp,
  FaBatteryThreeQuarters,
  faCircle,
} from "react-icons/fa6";
import { BtnDefault } from "../../../components/buttons/BtnDefault";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { Header } from "../../../components/Header";
import { useNavigate } from "react-router-dom";
import { useHookWaDevice } from "../../../hooks/useHookWaDevice";
import { WaDeviceSection } from "./number/sections/WaDevice";
import { WaLogActivitySection } from "./message/sections/WaLogActivity";
import { WaDeviceService } from "../../../services/WaDeviceService";


/* ============================================
   COMPONENT: Modal (Reusable Container)
   ============================================ */
const ModalWrapper = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        background: "rgba(0, 0, 0, 0.7)",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 transform scale-100"
        style={{
          background: "rgba(15, 23, 42, 0.95)",
          border: "1px solid rgba(51, 65, 85, 0.4)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Modal */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{
            borderBottom: "1px solid rgba(51, 65, 85, 0.3)",
            background: "rgba(15, 23, 42, 0.8)",
          }}
        >
          <h3 className="text-lg font-bold text-gray-100 flex items-center gap-2">
            <FaWhatsapp className="text-cyan-500" />
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1"
          >
            <FaXmark className="text-xl" />
          </button>
        </div>

        {/* Body Modal */}
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

/* ============================================
   MAIN COMPONENT: WhatsApp Sender Page
   ============================================ */
export const WhatsAppPage = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const navigate = useNavigate();
  
  // --- STATE MANAGEMENT ---
  const [activeModal, setActiveModal] = useState(null); // 'message' | 'addDevice' | null
  const [selectedDevice, setSelectedDevice] = useState(null);

  const [refreshHistory, setRefreshHistory] = useState(false);
  const [refreshWa, setRefreshWa] = useState(false);
  const [loading, setLoading] = useState(false);

  // Data Devices Dummy
  const [devices, setDevices] = useState([
    {
      id: 1,
      name: "Marketing Utama",
      phone: "6281234567890",
      status: "connected",
      battery: "85%",
    },
    {
      id: 2,
      name: "Support CS 1",
      phone: "6289876543210",
      status: "connected",
      battery: "42%",
    },
  ]);

  // State Form Pesan
  const [messageForm, setMessageForm] = useState({
    type: "private", // private | broadcast
    recipients: "",
    body: "",
  });

  // ✅ 2. TAMBAHKAN urlwebhook DI STATE FORM DEVICE
  const [deviceForm, setDeviceForm] = useState({
    name: "",
    phone: "",
    urlwebhook: "", // Ditambahkan sesuai kebutuhan backend
  });

  // --- HANDLERS ---

  const openMessageModal = (device) => {
    setSelectedDevice(device);
    setMessageForm({ type: "private", recipients: "", body: "" });
    setActiveModal("message");
  };

  const openAddDeviceModal = () => {
    // ✅ 3. RESET TERMASUK urlwebhook
    setDeviceForm({ name: "", phone: "", urlwebhook: "" });
    setActiveModal("addDevice");
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedDevice(null);
    setLoading(false);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulasi Kirim Pesan
    setTimeout(() => {
      alert(`Pesan berhasil dikirim dari ${selectedDevice?.name}!`);
      setLoading(false);
      closeModal();
    }, 1500);
  };

  // ✅ 4. IMPLEMENTASI API CREATE DEVICE
  const handleAddDevice = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Ambil data user dari localStorage untuk mendapatkan company_id
    // (Sesuaikan key 'user-radiustrend' dengan key yang kamu simpan di localStorage saat login)
    const userData = JSON.parse(localStorage.getItem('user-radiustrend') || '{}');
    const company_id = userData.company_id || '';

    // Mapping data frontend ke backend
    const payload = {
      company_id: company_id,
      name: deviceForm.name,
      sender: deviceForm.phone, // Frontend 'phone' -> Backend 'sender'
      urlwebhook: deviceForm.urlwebhook || null,
    };

    try {
      const response = await WaDeviceService.create({ data: payload });
      
      if (response.data.status) {
        alert(response.data.message || "Device berhasil ditambahkan!");
        
        // Trigger refresh jika ada hook/list device yang mengambil data dari API
        // setRefreshWa(prev => !prev); 
        
        closeModal();
      } else {
        alert(response.data.message || "Gagal menambahkan device.");
      }
    } catch (error) {
      console.error("Error create device:", error);
      let errorMessage = "Terjadi kesalahan pada server.";
      
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /* ============================================
     SUB-VIEW: ADD DEVICE FORM
     ============================================ */
  const renderAddDeviceContent = () => (
    <form onSubmit={handleAddDevice} className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
          Nama Perangkat
        </label>
        <input
          type="text"
          required
          value={deviceForm.name}
          onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
          className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 outline-none transition-all"
          placeholder="Contoh: HP Sales 1"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
          Nomor WhatsApp
        </label>
        <input
          type="text"
          required
          minLength={8} // Validasi frontend minimal 8 karakter sesuai backend
          value={deviceForm.phone}
          onChange={(e) => setDeviceForm({ ...deviceForm, phone: e.target.value })}
          className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 outline-none transition-all"
          placeholder="62812..."
        />
      </div>

      {/* ✅ 5. TAMBAHKAN INPUT WEBHOOK (OPSIONAL) */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
          URL Webhook <span className="text-slate-600 normal-case">(Opsional)</span>
        </label>
        <input
          type="url"
          value={deviceForm.urlwebhook}
          onChange={(e) => setDeviceForm({ ...deviceForm, urlwebhook: e.target.value })}
          className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 outline-none transition-all"
          placeholder="https://yourdomain.com/webhook"
        />
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="animate-spin inline-block w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full mb-2"></div>
          <p className="text-sm text-cyan-400 animate-pulse">Menyimpan perangkat...</p>
        </div>
      ) : (
        <button
          type="submit"
          className="w-full py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold transition-all shadow-lg shadow-cyan-600/25 flex items-center justify-center gap-2"
        >
          <FaPlus className="text-xs" /> Hubungkan Perangkat
        </button>
      )}
    </form>
  );

  /* ============================================
     SUB-VIEW: SEND MESSAGE FORM
     ============================================ */
  const renderMessageContent = () => (
    <form onSubmit={handleSendMessage} className="space-y-4">
      <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/50 flex items-center justify-between">
        <span className="text-sm text-slate-300">Menggunakan:</span>
        <span className="text-sm font-bold text-cyan-400">{selectedDevice?.name}</span>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
          Tipe Pengiriman
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label
            className={`cursor-pointer border rounded-xl p-3 flex flex-col items-center justify-center gap-2 transition-all ${
              messageForm.type === "private"
                ? "border-cyan-500 bg-cyan-500/10 text-cyan-400"
                : "border-slate-700 text-slate-400 hover:bg-slate-800"
            }`}
          >
            <input
              type="radio"
              name="msgType"
              value="private"
              checked={messageForm.type === "private"}
              onChange={() => setMessageForm({ ...messageForm, type: "private" })}
              className="hidden"
            />
            <i className="fa-regular fa-user"></i>
            <span className="text-xs font-bold">Private Chat</span>
          </label>

          <label
            className={`cursor-pointer border rounded-xl p-3 flex flex-col items-center justify-center gap-2 transition-all ${
              messageForm.type === "broadcast"
                ? "border-cyan-500 bg-cyan-500/10 text-cyan-400"
                : "border-slate-700 text-slate-400 hover:bg-slate-800"
            }`}
          >
            <input
              type="radio"
              name="msgType"
              value="broadcast"
              checked={messageForm.type === "broadcast"}
              onChange={() => setMessageForm({ ...messageForm, type: "broadcast" })}
              className="hidden"
            />
            <i className="fa-solid fa-bullhorn"></i>
            <span className="text-xs font-bold">Broadcast</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
          Nomor Tujuan
        </label>
        <textarea
          required
          rows={messageForm.type === "broadcast" ? 3 : 1}
          value={messageForm.recipients}
          onChange={(e) => setMessageForm({ ...messageForm, recipients: e.target.value })}
          className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 focus:border-cyan-500/50 outline-none transition-all"
          placeholder={messageForm.type === "broadcast" ? "628...\n628..." : "6281234567890"}
        />
        {messageForm.type === "broadcast" && (
          <p className="text-[10px] text-slate-500 mt-1">Pisahkan nomor dengan baris baru.</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
          Isi Pesan
        </label>
        <textarea
          required
          rows={4}
          value={messageForm.body}
          onChange={(e) => setMessageForm({ ...messageForm, body: e.target.value })}
          className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 focus:border-cyan-500/50 outline-none transition-all"
          placeholder="Tulis pesan anda disini..."
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:cursor-wait text-white font-semibold transition-all shadow-lg shadow-cyan-600/25 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <i className="fa-solid fa-circle-notch fa-spin"></i> Mengirim...
          </>
        ) : (
          <>
            <FaPaperPlane className="text-xs" /> Kirim Sekarang
          </>
        )}
      </button>
    </form>
  );

  /* ============================================
     MAIN RENDER
     ============================================ */
  return (
    <>
      <Helmet>
        <title>WhatsApp Gateway - DANELA NET</title>
        <meta
          name="description"
          content="Kelola data whatsapp gateway, mengirim pesan broadcast dan private."
        />
      </Helmet>
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              WhatsApp Gateway
            </h2>
          </div>
          <BtnDefault onClick={openAddDeviceModal}>
            <FaPlus className="text-xs hidden sm:block" />
            <span>Tambah Device</span>
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* Device Info */}
          <WaDeviceSection />

          {/* Log Activity */}
          <WaLogActivitySection />
        </main>

        {/* === MODAL: TAMBAH DEVICE === */}
        <ModalWrapper
          isOpen={activeModal === "addDevice"}
          onClose={closeModal}
          title="Hubungkan Perangkat Baru"
        >
          {renderAddDeviceContent()}
        </ModalWrapper>

        {/* === MODAL: KIRIM PESAN === */}
        <ModalWrapper
          isOpen={activeModal === "message"}
          onClose={closeModal}
          title="Kirim Pesan WhatsApp"
        >
          {renderMessageContent()}
        </ModalWrapper>
      </DashboardLayout>
    </>
  );
};

export default WhatsAppPage;